import { debounce } from '../debounce'
import getUrlDict from '../getUrlDict'
import parseURIComponent from '../parseUriComponent'
import jitsu from '../jitsu'
import { generateId, localStorageGet, setLottieScript } from '../utils'
import { updateFlowUserData } from './utils'
import replaceStorageURLsFromFirebaseToCloudflare from '../../api/helpers/replaceStorageURLsFromFirebaseToCloudflare'
import { Breakpoint } from '../../api/models/types'
// import ComputedField from '../computedFields'

interface UserData {
  entryId?: string
  current_page_id?: string
}

interface Flow {
  scroll_to_top_behavior?: 'instant' | 'smooth'
  scroll_to_document_top?: boolean
}

// const CAN_USE_LOCAL_STORAGE = true // Need to look more into this - compare with Vue engine

export function setResponsiveWatchers(
  element: HTMLElement,
  breakpoints?: Array<Breakpoint>,
  extraElements = []
) {
  const breakpointsWithDefaults = [
    { max_width: 720, name: 'tablet' },
    { max_width: 520, name: 'mobile' },
    ...(breakpoints || []),
  ]

  const updateBreakpoints = () => {
    const currentBreakpointGroups = breakpointsWithDefaults.filter(
      (b) => element.offsetWidth <= b.max_width
    )
    const attrValue = currentBreakpointGroups.map((b) => `${b.max_width}px`).join(' ')
    element.setAttribute('max-width', attrValue)
    extraElements.forEach((el: HTMLElement) => el.setAttribute('max-width', attrValue))
  }
  const updateBreakpointsDebounced = debounce(updateBreakpoints, 100)
  if (window.ResizeObserver) {
    const observer = new ResizeObserver(updateBreakpointsDebounced)
    observer.observe(element)
    return () => observer.disconnect()
  } else {
    // fallback with MutationObserver if Resize Observer is not supported by the browser
    const observer = new MutationObserver((mutations) =>
      mutations.forEach(
        (mutation) =>
          mutation.type === 'attributes' &&
          mutation.attributeName === 'style' &&
          updateBreakpointsDebounced()
      )
    )
    observer.observe(element, { attributeFilter: ['style'] })
    // document.body
    //   .querySelectorAll('*')
    //   .forEach((element) => observer.observe(element, { attributes: true }))
    window.addEventListener('resize', updateBreakpointsDebounced, false)
    updateBreakpointsDebounced()
    return () => {
      window.removeEventListener('resize', updateBreakpointsDebounced)
      observer.disconnect()
    }
  }
}

export function preloadPageImages(
  imagesToPreload?: string | string[],
  preloadedImages: HTMLLinkElement[] = []
) {
  if (!Array.isArray(imagesToPreload)) return preloadedImages

  const usedHrefs = new Set(preloadedImages.map((el: HTMLLinkElement) => el.href))
  const uniqueImagesToPreload = Array.from(new Set(imagesToPreload.filter((u) => u)))
  const preloadUrls = uniqueImagesToPreload.filter((url) => !usedHrefs.has(url))
  const links = preloadUrls.map((url) => {
    const src = replaceStorageURLsFromFirebaseToCloudflare(url) || url
    const link = document.createElement('link')

    //* If the image is a lottie animation, we construct the <link> element differently and set the lottie script
    if (src.includes('.json') || src.includes('lottie')) {
      setLottieScript()

      link.rel = 'preload'
      link.as = 'fetch'
      link.crossOrigin = 'anonymous'
      link.type = 'application/json'
      link.href = src
    } else {
      link.rel = 'prefetch'
      link.href = src
      link.as = 'image'
    }

    try {
      //* Before appending, check if the exact same link is already in the document to avoid unneeded links
      const existingLink = document.querySelector(`link[href="${src}"]`)
      if (existingLink) return existingLink as HTMLLinkElement

      document.head.appendChild(link)
    } catch (error) {
      console.error('Error appending link to document head', error)
    }

    return link
  })

  return [...preloadedImages, ...links]
}

export function scrollToTop(self: any, flow: Flow) {
  self.__scrollToTopCleanup?.()

  const useDocument = flow.scroll_to_document_top
  let rootEl = self.element
  if (useDocument) {
    rootEl = document.documentElement
  }

  const doScroll = () => {
    if (rootEl && rootEl.scrollIntoView)
      rootEl.scrollIntoView({ behavior: flow.scroll_to_top_behavior || 'smooth', inline: 'start' })
  }

  let fallbackTimeout: ReturnType<typeof setTimeout> | undefined
  const cleanup = () => {
    window.removeEventListener('embeddables:page_change_html_updated', onHtmlUpdated)
    if (fallbackTimeout !== undefined) clearTimeout(fallbackTimeout)
    self.__scrollToTopCleanup = undefined
  }

  //* Defer scroll until after page HTML is swapped to prevent iOS Safari
  //* from cancelling the smooth scroll when DOM mutations trigger layout recalculation.
  const onHtmlUpdated = () => {
    cleanup()
    requestAnimationFrame(doScroll)
  }
  window.addEventListener('embeddables:page_change_html_updated', onHtmlUpdated)

  //* Fallback if no page transition occurs (e.g. first page load, or called outside page changes)
  fallbackTimeout = setTimeout(() => {
    cleanup()
    requestAnimationFrame(doScroll)
  }, 400)

  self.__scrollToTopCleanup = cleanup
}

//*Moves focus to the form at the top of the page for accessibility.
export function focusTopOfPage(pageElement: Element): void {
  //* Form is the only element guaranteed to exist 
  const form =
    pageElement instanceof HTMLFormElement
      ? pageElement
      : (pageElement.parentElement?.querySelector('form') as HTMLFormElement | null)

  if (!form) return

  form.setAttribute('tabindex', '-1')

  //* Hide focus ring for this programmatic focus only; restore on blur so we don't show ring on page change
  const hideFocusRing = () => {
    form.style.setProperty('outline', 'none', 'important')
    form.style.setProperty('box-shadow', 'none', 'important')
  }
  const restoreFocusRing = () => {
    form.style.removeProperty('outline')
    form.style.removeProperty('box-shadow')
  }
  hideFocusRing()
  form.addEventListener('blur', restoreFocusRing, { once: true })
  form.focus()
  requestAnimationFrame(() => {
    hideFocusRing() 
  })
}

/**
 * Loads userData from localStorage as a property on a controller. Generally not used outside the main Flow controller
 * @param {*} self the controller instance
 * @param {*} id the id of the flow
 */
export function loadUserData(self: any, id: string) {
  /* @TODO - missing reset userdata, add referrer */
  const key = id || self.urlValue.split('/').slice(-1)[0]
  const userData: Partial<UserData> = {}
  if (self.flow?.defaults?.userData) {
    Object.assign(userData, self.flow.defaults.userData)
  }
  const localUserData =
    (!self.flow.forget_user_data &&
      JSON.parse(localStorageGet('SavvyFormUserData') || '{}')[key]) ||
    {}
  Object.assign(userData, localUserData)
  if (!userData.entryId) userData.entryId = generateId('entry')

  self.entryId = userData.entryId
  if (userData.current_page_id) self.pageIdValue = userData.current_page_id

  jitsu.set({ entry_id: userData.entryId })

  if (self.flow) {
    if (self.flow.url_keys) {
      const urlparams = getUrlDict()
      const urlKeys = Array.isArray(self.flow.url_keys)
        ? self.flow.url_keys
        : self.flow.url_keys.split('\n').map((t) => t.trim())
      urlKeys.forEach((k: string) => {
        if (urlparams[k]) {
          const val = parseURIComponent(urlparams[k])
          let value = val
          try {
            value = typeof val === 'string' ? JSON.parse(val) : val
            // Fixes issue where JSON.parse returns a number but the number is so large that it is approximated
            // E.g. to avoid 120213463854040164 being parsed as 120213463854040160
            if (typeof value === 'number' && (value as number).toString() !== val) {
              value = val
            }
          } catch (error) {
            value = val
          }
          if (!self.prefilledKeys) self.prefilledKeys = new Set()
          self.prefilledKeys.add(k)
          userData[k] = value
        }
      })
    }
    if (self.flow.embed_code_keys) {
      const attrs = {}
      const hostAttributes = self.element.closest('savvy')?.attributes
      if (hostAttributes) {
        for (let i = 0; i < hostAttributes.length; i++) {
          const { nodeName, nodeValue } = hostAttributes[i]
          attrs[nodeName] = nodeValue
        }
      }
      const embedCodeKeys = Array.isArray(self.flow.embed_code_keys)
        ? self.flow.embed_code_keys
        : self.flow.embed_code_keys.split('\n').map((t) => t.trim())
      embedCodeKeys.forEach((k: string) => {
        if (attrs[k]) {
          let value = attrs[k]
          try {
            value = typeof attrs[k] === 'string' ? JSON.parse(attrs[k]) : attrs[k]
          } catch (error) {
            value = attrs[k]
          }
          self.prefilledKeys.add(k)
          userData[k] = value
        }
      })
    }
  }
  self.updatePrefilledKeys(userData)

  const savvyEl = self.element.closest('[data-controller*="components--child-flow"]')
  if (savvyEl) {
    const childFlowData = self.application
      .getControllerForElementAndIdentifier(savvyEl, 'components--child-flow')
      .updateChildFlowUserData()
    Object.assign(userData, childFlowData)
  }
  // loadComputedFields(self, userData)
  updateFlowUserData(self, userData)
}

// function loadComputedFields(self, userData) {
//   const flow = self.flow || {}
//   const computedFields = flow.computedFields || []
//   self.computedFields = computedFields.map((cf) => {
//     const computedField = new ComputedField(cf, flow, self)
//     return computedField
//   })
//   self.evaluateComputedFields(userData)
// }
